import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    Cell,
} from "recharts";
import { Sprout, TrendingUp, Users2, Target } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { ChartCard } from "@/components/common/ChartCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtDecimal, fmtPct, chartColor, statusTone, youthPct, totalYouth } from "@/lib/format";

export default function Youth() {
    const { data } = useDashboard();
    const months = data.monthly_trend.months;
    const annual = data.full_year_activity_summary;
    const yri = annual.analytical_indices.youth_representation_index;
    const es = data.executive_summary;
    const districts = data.district_drilldown;

    const yriData = months.map((q) => ({
        month: q.month,
        YRI: q.analytical_indices.youth_representation_index.value,
        "Youth %": youthPct(q),
    }));

    const shareData = months.map((q) => {
        const ge = q.gender_engagement || {};
        const gentsBase = (ge.gents_senior || 0) + (ge.gents_youth || 0);
        const ladiesBase = (ge.ladies_senior || 0) + (ge.ladies_youth || 0);
        return {
            month: q.month,
            "Overall Youth %": youthPct(q),
            "Gents Youth %": gentsBase ? ((ge.gents_youth || 0) / gentsBase) * 100 : 0,
            "Ladies Youth %": ladiesBase ? ((ge.ladies_youth || 0) / ladiesBase) * 100 : 0,
        };
    });

    const attention = [...districts]
        .filter((d) => d.full_year.reported_activities > 0)
        .map((d) => ({
            name: d.district_name,
            yri: d.full_year.analytical_indices.youth_representation_index.value || 0,
            status: d.full_year.analytical_indices.youth_representation_index.status,
        }))
        .sort((a, b) => a.yri - b.yri)
        .slice(0, 8);

    const barColor = (status) => {
        const tone = statusTone(status);
        return {
            success: chartColor(3),
            warning: chartColor(1),
            destructive: chartColor(4),
            accent: chartColor(2),
            muted: chartColor(6),
        }[tone];
    };

    return (
        <div className="space-y-8">
            <PageHeader
                eyebrow="Youth Engagement"
                title="The next generation"
                description="Tracking how actively young members participate relative to their share of membership."
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard index={0} label="Youth Members" value={fmt(es.approved_youth_members)} sub={`${fmtPct(es.youth_share_pct)} of members`} icon={Sprout} tone="success" />
                <KpiCard index={1} label="Youth Attendance" value={fmt(totalYouth(annual))} sub="Full-year total" icon={Users2} tone="primary" />
                <KpiCard index={2} label="Youth Participation" value={fmtPct(youthPct(annual))} sub="Of all attendance" icon={TrendingUp} tone="accent" />
                <KpiCard
                    index={3}
                    label="Representation Index"
                    value={fmtDecimal(yri.value, 2)}
                    sub="1.0 = proportional"
                    icon={Target}
                    tone="success"
                    delta={<div className="mt-3"><StatusBadge status={yri.status} /></div>}
                />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard title="Youth Representation Index" description="Values above 1.0 mean youth are over-represented in attendance">
                    <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={yriData} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis {...axisProps} domain={[0, 1.3]} />
                                <ReferenceLine y={1} stroke={chartColor(3)} strokeDasharray="4 4" label={{ value: "Proportional", fill: "hsl(var(--muted-foreground))", fontSize: 11, position: "insideTopRight" }} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmtDecimal(v, 3)} />} />
                                <Line type="monotone" dataKey="YRI" stroke={chartColor(1)} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                <ChartCard title="Youth Participation Rate" description="Monthly youth participation with gents and ladies bifurcation">
                    <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={shareData} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis {...axisProps} unit="%" domain={[0, 40]} />
                                <ReferenceLine y={es.youth_share_pct} stroke={chartColor(2)} strokeDasharray="4 4" label={{ value: "Member share", fill: "hsl(var(--muted-foreground))", fontSize: 11, position: "insideTopRight" }} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmtPct(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                <Bar dataKey="Gents Youth %" fill={chartColor(1)} radius={[6, 6, 0, 0]} maxBarSize={26} />
                                <Bar dataKey="Ladies Youth %" fill={chartColor(2)} radius={[6, 6, 0, 0]} maxBarSize={26} />
                                <Line type="monotone" dataKey="Overall Youth %" stroke={chartColor(3)} strokeWidth={2.5} dot={{ r: 3 }} />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>
            </div>

            <Card className="border-border/70 bg-card p-5 shadow-card">
                <div className="mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Districts needing youth focus</h3>
                </div>
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={attention} layout="vertical" margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
                            <CartesianGrid {...gridProps} horizontal={false} vertical />
                            <XAxis type="number" {...axisProps} domain={[0, 1.2]} />
                            <YAxis type="category" dataKey="name" {...axisProps} width={110} />
                            <ReferenceLine x={1} stroke={chartColor(3)} strokeDasharray="4 4" />
                            <Tooltip content={<ChartTooltip formatter={(v) => fmtDecimal(v, 3)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                            <Bar dataKey="yri" name="YRI" radius={[0, 6, 6, 0]} maxBarSize={22}>
                                {attention.map((d, i) => <Cell key={i} fill={barColor(d.status)} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    Districts left of the dashed line have youth attending below their membership proportion, indicating room to deepen youth engagement.
                </p>
            </Card>
        </div>
    );
}
